import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';

// Relationship type display names
const RELATIONSHIP_TYPES = {
  one_to_one: 'One to One',
  one_to_many: 'One to Many',
  many_to_many: 'Many to Many'
};

// Confidence level colors
const getConfidenceColor = (confidence) => {
  if (confidence >= 0.9) return 'bg-green-500';
  if (confidence >= 0.7) return 'bg-yellow-500';
  return 'bg-orange-500';
};

const RelationshipEditor = () => {
  const { user, getCurrentCompanyId, getCurrentCompany } = useAuthStore();
  const companyId = getCurrentCompanyId();
  const currentCompany = getCurrentCompany();
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [relationships, setRelationships] = useState([]);
  const [wideViews, setWideViews] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState(null);
  const [showWideViewModal, setShowWideViewModal] = useState(false);
  const [showEditWideViewModal, setShowEditWideViewModal] = useState(false);
  const [editingWideView, setEditingWideView] = useState(null);
  const [selectedRelationships, setSelectedRelationships] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch relationships and data sources
  const fetchData = useCallback(async () => {
    if (!companyId) {
      setError('Please select a company first');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [relResponse, dsResponse] = await Promise.all([
        api.get('/relationships', { params: { companyId } }),
        api.get('/data-sources', { params: { companyId } })
      ]);
      
      setRelationships(relResponse.data.relationships || []);
      setWideViews(relResponse.data.wideViews || []);
      // Data sources API returns paginated response with 'data' property
      setDataSources(dsResponse.data.data || dsResponse.data.dataSources || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load relationships');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Run auto-detection
  const handleDetect = async () => {
    try {
      setDetecting(true);
      setError(null);
      const response = await api.post('/relationships/detect', { companyId });
      setSuccess(`Detected ${response.data.detected} relationships, added ${response.data.added} new ones`);
      await fetchData();
    } catch (err) {
      setError('Auto-detection failed');
      console.error(err);
    } finally {
      setDetecting(false);
    }
  };

  // Delete relationship
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this relationship?')) return;
    
    try {
      await api.delete(`/relationships/${id}`, { params: { companyId } });
      setRelationships(prev => prev.filter(r => r.id !== id));
      setSuccess('Relationship deleted');
    } catch (err) {
      setError('Failed to delete relationship');
      console.error(err);
    }
  };

  // Toggle relationship active status
  const handleToggleActive = async (id, isActive) => {
    try {
      await api.put(`/relationships/${id}`, { isActive: !isActive, companyId });
      setRelationships(prev => prev.map(r => 
        r.id === id ? { ...r, isActive: !isActive } : r
      ));
    } catch (err) {
      setError('Failed to update relationship');
      console.error(err);
    }
  };

  // Open edit modal
  const handleEdit = (relationship) => {
    setEditingRelationship(relationship);
    setShowEditModal(true);
  };

  // Update relationship
  const handleUpdate = async (id, data) => {
    try {
      await api.put(`/relationships/${id}`, { ...data, companyId });
      setRelationships(prev => prev.map(r => 
        r.id === id ? { ...r, ...data } : r
      ));
      setSuccess('Relationship updated');
      setShowEditModal(false);
      setEditingRelationship(null);
    } catch (err) {
      setError('Failed to update relationship');
      console.error(err);
    }
  };

  // Generate dbt models
  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError(null);
      const response = await api.post('/relationships/generate', { companyId });
      
      const stagingCount = response.data.staging?.generated || 0;
      const wideViewCount = response.data.wideViews?.generated || 0;
      const martsCount = response.data.marts?.generated || 0;
      const dbtRun = response.data.dbtRun;
      
      let message = `✓ Generated ${stagingCount} staging, ${wideViewCount} wide views, ${martsCount} marts`;
      
      // Add info about dbt execution
      if (dbtRun?.success) {
        if (dbtRun.method === 'direct') {
          message += `. ${dbtRun.message || 'Models materialized in BigQuery!'}`;
        } else if (dbtRun.runId) {
          message += `. dbt build triggered via Dagster (ID: ${dbtRun.runId})`;
        }
      } else if (dbtRun?.error) {
        message += `. ⚠️ dbt execution failed: ${dbtRun.hint || dbtRun.error}`;
      } else if (dbtRun?.modelsReady) {
        message += '. Models are ready - run dbt via Dagster UI or manually.';
      }
      
      setSuccess(message);
      await fetchData();
    } catch (err) {
      setError('Model generation failed');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  // Toggle relationship selection for wide view
  const toggleRelationshipSelection = (id) => {
    setSelectedRelationships(prev => 
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show message if no company selected (for super admins)
  if (!companyId) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-amber-800 dark:text-amber-300">No Company Selected</h3>
          <p className="mt-2 text-amber-700 dark:text-amber-400">
            Please select a company from the company selector to view and manage relationships.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          Data Connections
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 max-w-3xl">
          Connect your data tables together so you can analyze data across multiple sources. 
          For example, connect your orders to customers to see who bought what.
          {currentCompany && (
            <span className="block mt-1 text-sm text-blue-600 dark:text-blue-400">
              Company: {currentCompany.name || currentCompany}
            </span>
          )}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {success}
          <button onClick={() => setSuccess(null)} className="float-right">&times;</button>
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={handleDetect}
          disabled={detecting}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          {detecting ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              Detecting...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Auto-Detect Relationships
            </>
          )}
        </button>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Manual Relationship
        </button>

        <button
          onClick={() => setShowWideViewModal(true)}
          disabled={selectedRelationships.length === 0}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
          Create Wide View ({selectedRelationships.length} selected)
        </button>
      </div>

      {/* Relationships Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Relationships ({relationships.length})
          </h2>
        </div>
        
        {relationships.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <p className="mt-2">No relationships defined yet.</p>
            <p className="text-sm">Click "Auto-Detect" to find relationships or add them manually.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRelationships(relationships.map(r => r.id));
                        } else {
                          setSelectedRelationships([]);
                        }
                      }}
                      checked={selectedRelationships.length === relationships.length && relationships.length > 0}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Target
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {relationships.map((rel) => (
                  <tr key={rel.id} className={rel.isActive ? '' : 'opacity-50'}>
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedRelationships.includes(rel.id)}
                        onChange={() => toggleRelationshipSelection(rel.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {rel.sourceTable}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        .{rel.sourceColumn}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {rel.targetTable}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            .{rel.targetColumn}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 rounded">
                        {RELATIONSHIP_TYPES[rel.type] || rel.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-2 w-2 rounded-full ${getConfidenceColor(rel.confidence)} mr-2`}></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {Math.round(rel.confidence * 100)}%
                        </span>
                        {rel.isAutoDetected && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                            Auto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(rel.id, rel.isActive)}
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          rel.isActive 
                            ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {rel.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(rel)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit relationship"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(rel.id)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete relationship"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Wide Views Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Wide Views ({wideViews.length})
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Pre-joined views that combine related tables for easier querying.
          </p>
        </div>
        
        {wideViews.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <p>No wide views created yet.</p>
            <p className="text-sm">Select relationships and click "Create Wide View" to combine tables.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {wideViews.map((view, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-purple-300 dark:hover:border-purple-700 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">{view.name}</h3>
                  <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded ${
                    view.status === 'ready' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : view.status === 'error'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                  }`}>
                    {view.status}
                  </span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Base: {view.baseTable}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Joins: {view.includedTables?.map(t => t.table).join(', ') || 'N/A'}
                </p>
                {view.selectedColumns?.length > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Columns: {view.selectedColumns.length} selected
                  </p>
                )}
                {view.calculatedColumns?.length > 0 && (
                  <div className="mt-1">
                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      + {view.calculatedColumns.length} calculated: {view.calculatedColumns.map(c => c.name).join(', ')}
                    </p>
                  </div>
                )}
                {view.dbtModelName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    dbt: {view.dbtModelName}
                  </p>
                )}
                
                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setEditingWideView(view);
                      setShowEditWideViewModal(true);
                    }}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded flex items-center gap-1"
                    title="Edit wide view"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Are you sure you want to delete the wide view "${view.name}"?`)) return;
                      try {
                        await api.delete(`/relationships/wide-view/${encodeURIComponent(view.name)}`, { params: { companyId } });
                        setSuccess('Wide view deleted');
                        await fetchData();
                      } catch (err) {
                        setError('Failed to delete wide view');
                      }
                    }}
                    className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded flex items-center gap-1"
                    title="Delete wide view"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Relationship Modal */}
      {showAddModal && (
        <RelationshipModal
          mode="add"
          dataSources={dataSources}
          onClose={() => setShowAddModal(false)}
          onSave={async (data) => {
            try {
              await api.post('/relationships', { ...data, companyId });
              setSuccess('Relationship added');
              setShowAddModal(false);
              await fetchData();
            } catch (err) {
              setError('Failed to add relationship');
            }
          }}
        />
      )}

      {/* Edit Relationship Modal */}
      {showEditModal && editingRelationship && (
        <RelationshipModal
          mode="edit"
          dataSources={dataSources}
          relationship={editingRelationship}
          onClose={() => {
            setShowEditModal(false);
            setEditingRelationship(null);
          }}
          onSave={(data) => handleUpdate(editingRelationship.id, data)}
        />
      )}

      {/* Create Wide View Modal */}
      {showWideViewModal && (
        <CreateWideViewModal
          relationships={relationships.filter(r => selectedRelationships.includes(r.id))}
          dataSources={dataSources}
          onClose={() => setShowWideViewModal(false)}
          onCreate={async (data) => {
            try {
              await api.post('/relationships/wide-view', {
                ...data,
                companyId,
                relationshipIds: selectedRelationships
              });
              setSuccess('Wide view created');
              setShowWideViewModal(false);
              setSelectedRelationships([]);
              await fetchData();
            } catch (err) {
              setError('Failed to create wide view');
            }
          }}
        />
      )}

      {/* Edit Wide View Modal */}
      {showEditWideViewModal && editingWideView && (
        <EditWideViewModal
          wideView={editingWideView}
          relationships={relationships}
          dataSources={dataSources}
          onClose={() => {
            setShowEditWideViewModal(false);
            setEditingWideView(null);
          }}
          onSave={async (data) => {
            try {
              await api.put(`/relationships/wide-view/${encodeURIComponent(editingWideView.name)}`, {
                ...data,
                companyId
              });
              setSuccess('Wide view updated');
              setShowEditWideViewModal(false);
              setEditingWideView(null);
              await fetchData();
            } catch (err) {
              setError('Failed to update wide view');
            }
          }}
        />
      )}
    </div>
  );
};

// Relationship Modal Component (Add/Edit)
const RelationshipModal = ({ mode = 'add', dataSources, relationship = null, onClose, onSave }) => {
  const isEdit = mode === 'edit';
  
  const [formData, setFormData] = useState({
    sourceTable: relationship?.sourceTable || '',
    sourceColumn: relationship?.sourceColumn || '',
    targetTable: relationship?.targetTable || '',
    targetColumn: relationship?.targetColumn || '',
    type: relationship?.type || 'one_to_many',
    description: relationship?.description || ''
  });

  const tables = dataSources.map(ds => ({
    name: ds.bigQueryTable,
    columns: ds.schemaInfo?.tables?.[0]?.columns || []
  }));

  const sourceColumns = tables.find(t => t.name === formData.sourceTable)?.columns || [];
  const targetColumns = tables.find(t => t.name === formData.targetTable)?.columns || [];

  // Check if the current column value exists in the available columns
  // This handles cases where the table exists but maybe columns aren't loaded yet
  const isValidSourceColumn = !formData.sourceColumn || sourceColumns.some(c => c.name === formData.sourceColumn);
  const isValidTargetColumn = !formData.targetColumn || targetColumns.some(c => c.name === formData.targetColumn);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Relationship' : 'Add Relationship'}
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Table</label>
              <select
                value={formData.sourceTable}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceTable: e.target.value, sourceColumn: '' }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              >
                <option value="">Select table</option>
                {tables.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.sourceTable && !tables.find(t => t.name === formData.sourceTable) && (
                  <option value={formData.sourceTable}>{formData.sourceTable}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Column</label>
              <select
                value={formData.sourceColumn}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceColumn: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                disabled={!formData.sourceTable}
              >
                <option value="">Select column</option>
                {sourceColumns.map(c => (
                  <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.sourceColumn && !sourceColumns.find(c => c.name === formData.sourceColumn) && (
                  <option value={formData.sourceColumn}>{formData.sourceColumn}</option>
                )}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Table</label>
              <select
                value={formData.targetTable}
                onChange={(e) => setFormData(prev => ({ ...prev, targetTable: e.target.value, targetColumn: '' }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              >
                <option value="">Select table</option>
                {tables.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.targetTable && !tables.find(t => t.name === formData.targetTable) && (
                  <option value={formData.targetTable}>{formData.targetTable}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Column</label>
              <select
                value={formData.targetColumn}
                onChange={(e) => setFormData(prev => ({ ...prev, targetColumn: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
                disabled={!formData.targetTable}
              >
                <option value="">Select column</option>
                {targetColumns.map(c => (
                  <option key={c.name} value={c.name}>{c.name} ({c.type})</option>
                ))}
                {/* Show current value if not in list (for edit mode) */}
                {isEdit && formData.targetColumn && !targetColumns.find(c => c.name === formData.targetColumn) && (
                  <option value={formData.targetColumn}>{formData.targetColumn}</option>
                )}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Relationship Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            >
              {Object.entries(RELATIONSHIP_TYPES).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="e.g., Links orders to customers"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={!formData.sourceTable || !formData.sourceColumn || !formData.targetTable || !formData.targetColumn}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg"
          >
            {isEdit ? 'Save Changes' : 'Add Relationship'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Data types for calculated columns
const DATA_TYPES = [
  { value: 'FLOAT', label: 'Decimal (FLOAT)' },
  { value: 'INTEGER', label: 'Whole Number (INTEGER)' },
  { value: 'STRING', label: 'Text (STRING)' },
  { value: 'BOOLEAN', label: 'True/False (BOOLEAN)' },
  { value: 'TIMESTAMP', label: 'Date & Time (TIMESTAMP)' },
  { value: 'DATE', label: 'Date (DATE)' }
];

// Example expressions for calculated columns
const EXPRESSION_EXAMPLES = [
  { label: 'Sum', example: 'column1 + column2' },
  { label: 'Difference', example: 'column1 - column2' },
  { label: 'Multiply', example: 'column1 * column2' },
  { label: 'Divide', example: 'column1 / NULLIF(column2, 0)' },
  { label: 'Percentage', example: 'ROUND(column1 / column2 * 100, 2)' },
  { label: 'Concat', example: "CONCAT(column1, ' - ', column2)" },
  { label: 'Date Diff Days', example: 'DATE_DIFF(end_date, start_date, DAY)' },
  { label: 'Case When', example: "CASE WHEN column1 > 100 THEN 'High' ELSE 'Low' END" }
];

// Create Wide View Modal Component
const CreateWideViewModal = ({ relationships, dataSources, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [baseTable, setBaseTable] = useState('');
  const [calculatedColumns, setCalculatedColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState({}); // { tableName: ['col1', 'col2'] }
  const [selectAllTables, setSelectAllTables] = useState({}); // { tableName: true/false }
  const [showAddCalcColumn, setShowAddCalcColumn] = useState(false);
  const [newCalcColumn, setNewCalcColumn] = useState({
    name: '',
    expression: '',
    description: '',
    dataType: 'FLOAT'
  });

  // Get unique tables from relationships
  const tables = [...new Set([
    ...relationships.map(r => r.sourceTable),
    ...relationships.map(r => r.targetTable)
  ])];

  // Get columns for a table from dataSources
  const getColumnsForTable = (tableName) => {
    const ds = dataSources.find(d => d.bigQueryTable === tableName);
    return ds?.schemaInfo?.tables?.[0]?.columns || [];
  };

  // Initialize selectAllTables when baseTable changes
  React.useEffect(() => {
    if (baseTable) {
      const initialSelectAll = {};
      const initialSelected = {};
      tables.forEach(t => {
        initialSelectAll[t] = true; // Default to all selected
        initialSelected[t] = getColumnsForTable(t).map(c => c.name);
      });
      setSelectAllTables(initialSelectAll);
      setSelectedColumns(initialSelected);
    }
  }, [baseTable]);

  // Toggle all columns for a table
  const handleToggleAllColumns = (tableName, checked) => {
    setSelectAllTables(prev => ({ ...prev, [tableName]: checked }));
    if (checked) {
      const allCols = getColumnsForTable(tableName).map(c => c.name);
      setSelectedColumns(prev => ({ ...prev, [tableName]: allCols }));
    } else {
      setSelectedColumns(prev => ({ ...prev, [tableName]: [] }));
    }
  };

  // Toggle individual column
  const handleToggleColumn = (tableName, columnName, checked) => {
    setSelectedColumns(prev => {
      const current = prev[tableName] || [];
      if (checked) {
        return { ...prev, [tableName]: [...current, columnName] };
      } else {
        return { ...prev, [tableName]: current.filter(c => c !== columnName) };
      }
    });
    // Update selectAll state
    const allCols = getColumnsForTable(tableName).map(c => c.name);
    const newSelected = selectedColumns[tableName] || [];
    if (checked) {
      setSelectAllTables(prev => ({ ...prev, [tableName]: newSelected.length + 1 === allCols.length }));
    } else {
      setSelectAllTables(prev => ({ ...prev, [tableName]: false }));
    }
  };

  // Build selectedColumns array for API
  const buildSelectedColumnsArray = () => {
    const result = [];
    for (const [tableName, columns] of Object.entries(selectedColumns)) {
      for (const col of columns) {
        result.push({
          source: tableName === baseTable ? 'base' : tableName,
          column: col,
          alias: null // Could add alias support later
        });
      }
    }
    return result;
  };

  // Count total selected columns
  const getTotalSelectedColumns = () => {
    return Object.values(selectedColumns).reduce((sum, cols) => sum + cols.length, 0);
  };

  const handleAddCalculatedColumn = () => {
    if (!newCalcColumn.name || !newCalcColumn.expression) return;
    
    setCalculatedColumns(prev => [...prev, { ...newCalcColumn }]);
    setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
    setShowAddCalcColumn(false);
  };

  const handleRemoveCalculatedColumn = (index) => {
    setCalculatedColumns(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Wide View</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Combine tables and add calculated columns for derived metrics.
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">View Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., customer_orders_complete"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Base Table</label>
            <select
              value={baseTable}
              onChange={(e) => setBaseTable(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            >
              <option value="">Select base table</option>
              {tables.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            </div>
          </div>

          {/* Selected Relationships */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selected Relationships</label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {relationships.map((rel, index) => (
                <div key={index} className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {rel.sourceTable}.{rel.sourceColumn} → {rel.targetTable}.{rel.targetColumn}
                </div>
              ))}
            </div>
          </div>

          {/* Column Selection Section */}
          {baseTable && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Columns</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Choose which columns to include from each table ({getTotalSelectedColumns()} selected)
                  </p>
          </div>
        </div>

              <div className="space-y-4 max-h-64 overflow-y-auto">
                {/* Base Table Columns */}
                <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
                  <div className="bg-purple-50 dark:bg-purple-900/30 px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded">BASE</span>
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{baseTable}</span>
                      <span className="text-xs text-gray-500">
                        ({(selectedColumns[baseTable] || []).length}/{getColumnsForTable(baseTable).length} columns)
                      </span>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectAllTables[baseTable] || false}
                        onChange={(e) => handleToggleAllColumns(baseTable, e.target.checked)}
                        className="rounded text-purple-600"
                      />
                      <span className="text-gray-600 dark:text-gray-400">All</span>
                    </label>
                  </div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                    {getColumnsForTable(baseTable).map((col) => (
                      <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={(selectedColumns[baseTable] || []).includes(col.name)}
                          onChange={(e) => handleToggleColumn(baseTable, col.name, e.target.checked)}
                          className="rounded text-purple-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                          {col.name}
                        </span>
                        <span className="text-xs text-gray-400 hidden md:inline">{col.type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Joined Tables Columns */}
                {tables.filter(t => t !== baseTable).map((tableName) => (
                  <div key={tableName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">JOIN</span>
                        <span className="font-medium text-gray-900 dark:text-white text-sm">{tableName}</span>
                        <span className="text-xs text-gray-500">
                          ({(selectedColumns[tableName] || []).length}/{getColumnsForTable(tableName).length} columns)
                        </span>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectAllTables[tableName] || false}
                          onChange={(e) => handleToggleAllColumns(tableName, e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-gray-600 dark:text-gray-400">All</span>
                      </label>
                    </div>
                    <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                      {getColumnsForTable(tableName).map((col) => (
                        <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                          <input
                            type="checkbox"
                            checked={(selectedColumns[tableName] || []).includes(col.name)}
                            onChange={(e) => handleToggleColumn(tableName, col.name, e.target.checked)}
                            className="rounded text-blue-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                            {col.name}
                          </span>
                          <span className="text-xs text-gray-400 hidden md:inline">{col.type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calculated Columns Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Calculated Columns</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Add derived columns using SQL expressions</p>
              </div>
              <button
                onClick={() => setShowAddCalcColumn(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Column
              </button>
            </div>

            {/* List of calculated columns */}
            {calculatedColumns.length > 0 && (
              <div className="space-y-2 mb-4">
                {calculatedColumns.map((col, index) => (
                  <div key={index} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-blue-800 dark:text-blue-300">{col.name}</span>
                          <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400 rounded">
                            {col.dataType}
                          </span>
                        </div>
                        <code className="text-xs text-gray-600 dark:text-gray-400 block mt-1 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {col.expression}
                        </code>
                        {col.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{col.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveCalculatedColumn(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {calculatedColumns.length === 0 && !showAddCalcColumn && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                No calculated columns added yet.
              </div>
            )}

            {/* Add calculated column form */}
            {showAddCalcColumn && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Column Name</label>
                    <input
                      type="text"
                      value={newCalcColumn.name}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') }))}
                      placeholder="e.g., total_revenue"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Type</label>
                    <select
                      value={newCalcColumn.dataType}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, dataType: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    >
                      {DATA_TYPES.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SQL Expression</label>
                  <textarea
                    value={newCalcColumn.expression}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, expression: e.target.value }))}
                    placeholder="e.g., price * quantity"
                    rows={2}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-700"
                  />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {EXPRESSION_EXAMPLES.map(ex => (
                      <button
                        key={ex.label}
                        type="button"
                        onClick={() => setNewCalcColumn(prev => ({ ...prev, expression: ex.example }))}
                        className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
                      >
                        {ex.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newCalcColumn.description}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Total revenue from order"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAddCalcColumn(false);
                      setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
                    }}
                    className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCalculatedColumn}
                    disabled={!newCalcColumn.name || !newCalcColumn.expression}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm rounded"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => onCreate({ 
              name, 
              baseTable, 
              calculatedColumns,
              selectedColumns: buildSelectedColumnsArray()
            })}
            disabled={!name || !baseTable || getTotalSelectedColumns() === 0}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg"
          >
            Create Wide View ({getTotalSelectedColumns()} columns)
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Wide View Modal Component
const EditWideViewModal = ({ wideView, relationships, dataSources, onClose, onSave }) => {
  const [calculatedColumns, setCalculatedColumns] = useState(wideView.calculatedColumns || []);
  const [joinTypes, setJoinTypes] = useState(() => {
    const types = {};
    (wideView.includedTables || []).forEach(t => {
      types[t.relationshipId] = t.joinType || 'LEFT';
    });
    return types;
  });
  
  // Get all tables in this wide view
  const allTables = [wideView.baseTable, ...(wideView.includedTables || []).map(t => t.table)];
  
  // Get columns for a table from dataSources
  const getColumnsForTable = (tableName) => {
    const ds = dataSources.find(d => d.bigQueryTable === tableName);
    return ds?.schemaInfo?.tables?.[0]?.columns || [];
  };
  
  // Initialize selectedColumns from existing wideView.selectedColumns
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const initial = {};
    allTables.forEach(t => {
      initial[t] = [];
    });
    // If there are existing selectedColumns, populate them
    if (wideView.selectedColumns && wideView.selectedColumns.length > 0) {
      wideView.selectedColumns.forEach(sc => {
        const tableName = sc.source === 'base' ? wideView.baseTable : sc.source;
        if (initial[tableName]) {
          initial[tableName].push(sc.column);
        }
      });
    } else {
      // Default: all columns selected
      allTables.forEach(t => {
        initial[t] = getColumnsForTable(t).map(c => c.name);
      });
    }
    return initial;
  });
  
  const [selectAllTables, setSelectAllTables] = useState(() => {
    const initial = {};
    allTables.forEach(t => {
      const allCols = getColumnsForTable(t).map(c => c.name);
      initial[t] = selectedColumns[t]?.length === allCols.length;
    });
    return initial;
  });
  
  const [showAddCalcColumn, setShowAddCalcColumn] = useState(false);
  const [newCalcColumn, setNewCalcColumn] = useState({
    name: '',
    expression: '',
    description: '',
    dataType: 'FLOAT'
  });

  // Toggle all columns for a table
  const handleToggleAllColumns = (tableName, checked) => {
    setSelectAllTables(prev => ({ ...prev, [tableName]: checked }));
    if (checked) {
      const allCols = getColumnsForTable(tableName).map(c => c.name);
      setSelectedColumns(prev => ({ ...prev, [tableName]: allCols }));
    } else {
      setSelectedColumns(prev => ({ ...prev, [tableName]: [] }));
    }
  };

  // Toggle individual column
  const handleToggleColumn = (tableName, columnName, checked) => {
    setSelectedColumns(prev => {
      const current = prev[tableName] || [];
      if (checked) {
        return { ...prev, [tableName]: [...current, columnName] };
      } else {
        return { ...prev, [tableName]: current.filter(c => c !== columnName) };
      }
    });
  };

  // Build selectedColumns array for API
  const buildSelectedColumnsArray = () => {
    const result = [];
    for (const [tableName, columns] of Object.entries(selectedColumns)) {
      for (const col of columns) {
        result.push({
          source: tableName === wideView.baseTable ? 'base' : tableName,
          column: col,
          alias: null
        });
      }
    }
    return result;
  };

  // Count total selected columns
  const getTotalSelectedColumns = () => {
    return Object.values(selectedColumns).reduce((sum, cols) => sum + cols.length, 0);
  };

  const handleAddCalculatedColumn = () => {
    if (!newCalcColumn.name || !newCalcColumn.expression) return;
    setCalculatedColumns(prev => [...prev, { ...newCalcColumn }]);
    setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
    setShowAddCalcColumn(false);
  };

  const handleRemoveCalculatedColumn = (index) => {
    setCalculatedColumns(prev => prev.filter((_, i) => i !== index));
  };

  const handleJoinTypeChange = (relationshipId, newType) => {
    setJoinTypes(prev => ({ ...prev, [relationshipId]: newType }));
  };

  // Get relationship details for included tables
  const getRelationshipForTable = (included) => {
    return relationships.find(r => r.id === included.relationshipId);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Wide View: {wideView.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Modify calculated columns and join types.
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* View Info (Read-only) */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Name:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">{wideView.name}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Status:</span>
                <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                  wideView.status === 'ready' 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                }`}>
                  {wideView.status}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 dark:text-gray-400">Base Table:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">{wideView.baseTable}</span>
              </div>
              {wideView.dbtModelName && (
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">dbt Model:</span>
                  <span className="ml-2 font-mono text-sm text-purple-600 dark:text-purple-400">{wideView.dbtModelName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Included Tables with Join Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Included Tables & Join Types</label>
            <div className="space-y-2">
              {(wideView.includedTables || []).map((included, index) => {
                const rel = getRelationshipForTable(included);
                return (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                    <div className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <span className="font-medium text-gray-900 dark:text-white">{included.table}</span>
                      {rel && (
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          via {rel.sourceColumn} → {rel.targetColumn}
                        </span>
                      )}
                    </div>
                    <select
                      value={joinTypes[included.relationshipId] || 'LEFT'}
                      onChange={(e) => handleJoinTypeChange(included.relationshipId, e.target.value)}
                      className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700"
                    >
                      <option value="LEFT">LEFT JOIN</option>
                      <option value="INNER">INNER JOIN</option>
                      <option value="RIGHT">RIGHT JOIN</option>
                      <option value="FULL">FULL JOIN</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Column Selection Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Columns</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Choose which columns to include ({getTotalSelectedColumns()} selected)
                </p>
              </div>
            </div>

            <div className="space-y-4 max-h-64 overflow-y-auto">
              {/* Base Table Columns */}
              <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
                <div className="bg-purple-50 dark:bg-purple-900/30 px-3 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-xs bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded">BASE</span>
                    <span className="font-medium text-gray-900 dark:text-white text-sm">{wideView.baseTable}</span>
                    <span className="text-xs text-gray-500">
                      ({(selectedColumns[wideView.baseTable] || []).length}/{getColumnsForTable(wideView.baseTable).length})
                    </span>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectAllTables[wideView.baseTable] || false}
                      onChange={(e) => handleToggleAllColumns(wideView.baseTable, e.target.checked)}
                      className="rounded text-purple-600"
                    />
                    <span className="text-gray-600 dark:text-gray-400">All</span>
                  </label>
                </div>
                <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                  {getColumnsForTable(wideView.baseTable).map((col) => (
                    <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={(selectedColumns[wideView.baseTable] || []).includes(col.name)}
                        onChange={(e) => handleToggleColumn(wideView.baseTable, col.name, e.target.checked)}
                        className="rounded text-purple-600"
                      />
                      <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                        {col.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Joined Tables Columns */}
              {(wideView.includedTables || []).map((included) => (
                <div key={included.table} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">JOIN</span>
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{included.table}</span>
                      <span className="text-xs text-gray-500">
                        ({(selectedColumns[included.table] || []).length}/{getColumnsForTable(included.table).length})
                      </span>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectAllTables[included.table] || false}
                        onChange={(e) => handleToggleAllColumns(included.table, e.target.checked)}
                        className="rounded text-blue-600"
                      />
                      <span className="text-gray-600 dark:text-gray-400">All</span>
                    </label>
                  </div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto bg-white dark:bg-gray-800">
                    {getColumnsForTable(included.table).map((col) => (
                      <label key={col.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={(selectedColumns[included.table] || []).includes(col.name)}
                          onChange={(e) => handleToggleColumn(included.table, col.name, e.target.checked)}
                          className="rounded text-blue-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300 truncate" title={`${col.name} (${col.type})`}>
                          {col.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Calculated Columns Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Calculated Columns</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Add or remove derived columns using SQL expressions</p>
              </div>
              <button
                onClick={() => setShowAddCalcColumn(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Column
              </button>
            </div>

            {/* List of calculated columns */}
            {calculatedColumns.length > 0 && (
              <div className="space-y-2 mb-4">
                {calculatedColumns.map((col, index) => (
                  <div key={index} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-blue-800 dark:text-blue-300">{col.name}</span>
                          <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400 rounded">
                            {col.dataType}
                          </span>
                        </div>
                        <code className="text-xs text-gray-600 dark:text-gray-400 block mt-1 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {col.expression}
                        </code>
                        {col.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{col.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveCalculatedColumn(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {calculatedColumns.length === 0 && !showAddCalcColumn && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                No calculated columns.
              </div>
            )}

            {/* Add calculated column form */}
            {showAddCalcColumn && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Column Name</label>
                    <input
                      type="text"
                      value={newCalcColumn.name}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') }))}
                      placeholder="e.g., total_revenue"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Type</label>
                    <select
                      value={newCalcColumn.dataType}
                      onChange={(e) => setNewCalcColumn(prev => ({ ...prev, dataType: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                    >
                      {DATA_TYPES.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">SQL Expression</label>
                  <textarea
                    value={newCalcColumn.expression}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, expression: e.target.value }))}
                    placeholder="e.g., price * quantity"
                    rows={2}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-700"
                  />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {EXPRESSION_EXAMPLES.map(ex => (
                      <button
                        key={ex.label}
                        type="button"
                        onClick={() => setNewCalcColumn(prev => ({ ...prev, expression: ex.example }))}
                        className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
                      >
                        {ex.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newCalcColumn.description}
                    onChange={(e) => setNewCalcColumn(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g., Total revenue from order"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAddCalcColumn(false);
                      setNewCalcColumn({ name: '', expression: '', description: '', dataType: 'FLOAT' });
                    }}
                    className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCalculatedColumn}
                    disabled={!newCalcColumn.name || !newCalcColumn.expression}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm rounded"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between sticky bottom-0 bg-white dark:bg-gray-800">
          <p className="text-xs text-gray-500 dark:text-gray-400 self-center">
            Changes will require regenerating dbt models.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave({ 
                calculatedColumns, 
                joinTypes,
                selectedColumns: buildSelectedColumnsArray()
              })}
              disabled={getTotalSelectedColumns() === 0}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg"
            >
              Save Changes ({getTotalSelectedColumns()} columns)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelationshipEditor;

